import { BarsLegend } from '@components/graphComponents/BarsLegend';
import XAxis from '@components/graphComponents/DatedXAxis';
import GraphDataProvider, { GraphContext } from '@components/graphComponents/GraphDataProvider';
import { GroupedBars } from '@components/graphComponents/GroupedBars';
import { Alignments, Overlay } from '@components/graphComponents/Overlay';
import { DataEntry, GraphType, YAxis } from '@components/graphComponents/types';
import { HOVER_INFO_HEIGHT, HOVER_Y_OFFSET } from '@components/graphComponents/utils';
import {
    generateTicksFromSeconds,
    YAxisLines,
    YAxisTicks,
} from '@components/graphComponents/YAxis';
import { DieselAndElectric, Formatter, Section } from '@components/summary/types';
import SegmentableDay from '@features/ui/SegmentableDay';
import SegmentableDayOverlay from '@features/ui/SegmentableDayOverlay';
import { HoverInfo } from '@pages/driverAnalysis/types';
import { VehicleGraphDimensions } from '@pages/vehicleAnalysis/vehicleDataGraph';
import { formatSecondsToTime } from '@utils/stringFormatters';
import _ from 'lodash';
import { useState } from 'react';
import { FormattedMessage, IntlFormatters } from 'react-intl';

import { HOURS } from '../../constants/units';
import { DateRange } from '../../types';
import { BARS_INFO, generateTicks } from './utils';

const dieselAndElectricBarsSchema = (scaleName: YAxis) => {
    switch (scaleName) {
        case 'leftUpY':
            return BARS_INFO.ICE.colorValue;
        case 'rightUpY':
            return BARS_INFO.BEV_UP.colorValue;
        case 'rightDownY':
            return BARS_INFO.BEV_DOWN.colorValue;
        default:
            return '';
    }
};

const TwoUpBarsGraph = ({
    column,
    data,
    dateRange,
    dimensions,
    intl: { formatNumber = (value: number) => value.toString(), formatDate, formatMessage },
    languageData,
    tickFormatter,
}: {
    column: Section;
    data: DataEntry[];
    dateRange: DateRange;
    dimensions: VehicleGraphDimensions;
    intl: Omit<IntlFormatters, 'formatNumber'> & { formatNumber: (value: number) => string };
    languageData: Record<string, string>;
    tickFormatter: (dateRange: DateRange) => (d: Date) => string;
}) => {
    const [hoverInfo, setHover] = useState<HoverInfo | null>(null);
    const [hoverInfoXOffset, setHoverInfoXOffset] = useState<number>(0);

    const leftYScaleFormatter = (column.formatter as DieselAndElectric<Formatter>).diesel;
    const leftYScaleUnit = (column.unit as DieselAndElectric<string>).diesel;
    const rightYScaleFormatter = (column.formatter as DieselAndElectric<Formatter>).electric;
    const rightYScaleUnit = (column.unit as DieselAndElectric<string>).electric;
    const hasDifferentUnits = leftYScaleUnit !== rightYScaleUnit;

    const hasLeftBarData = _.get(hoverInfo, 'data.leftUpY', 0) > 0;
    const hasRightBarData = _.get(hoverInfo, 'data.rightUpY', 0) > 0;
    const hasNoData = !hasLeftBarData && !hasRightBarData;
    const hasSomeData = hasLeftBarData || hasRightBarData;

    const twoUpBarsInfo = {
        ICE: {
            label: _.get(languageData, 'vehicleFuelType.fuel'),
            color: BARS_INFO.ICE.colorValue,
        },
        BEV_UP: {
            label: _.get(languageData, 'vehicleFuelType.electric'),
            color: BARS_INFO.BEV_UP.colorValue,
        },
    };

    const leftTickGenerator = leftYScaleUnit === HOURS ? generateTicksFromSeconds : generateTicks;
    const leftFormatter = leftYScaleUnit === HOURS ? formatSecondsToTime : formatNumber;
    const rightFormatter = rightYScaleUnit === HOURS ? formatSecondsToTime : formatNumber;

    const getOverlayY = (y: number) =>
        y + HOVER_INFO_HEIGHT > dimensions.height - dimensions.margin
            ? dimensions.height - dimensions.margin - HOVER_INFO_HEIGHT
            : y;

    const renderLeftOverlay = () =>
        hasLeftBarData && (
            <Overlay
                withDataClasses={`border-color-${BARS_INFO.ICE.colorName} text-color-${BARS_INFO.ICE.colorName} bg-white`}
                hasData
                maxXValue={dimensions.width}
                x={hoverInfo!.leftX}
                y={getOverlayY(hoverInfo!.leftUpY)}
                alignment={Alignments.RIGHT}
            >
                {leftYScaleFormatter(hoverInfo!.data.leftUpY)}
            </Overlay>
        );

    const renderRightOverlay = () =>
        hasRightBarData && (
            <Overlay
                hasData
                maxXValue={dimensions.width}
                x={hoverInfo!.rightX}
                y={getOverlayY(hoverInfo!.rightUpY)}
                useCustomStyleColors
                style={{
                    borderColor: BARS_INFO.BEV_UP.colorValue,
                    color: BARS_INFO.BEV_UP.colorValue,
                    backgroundColor: '#fff',
                }}
            >
                {rightYScaleFormatter(hoverInfo!.data.rightUpY)}
            </Overlay>
        );

    const renderRightYAxis = () => (
        <GraphContext.Consumer>
            {({ leftYScale, rightYScale }) => {
                let customTicksYValues;
                if (hasDifferentUnits && leftYScale && rightYScale) {
                    const leftTicks = leftTickGenerator(leftYScale, dimensions.height);
                    customTicksYValues = leftTicks
                        .map((tick) => leftYScale(tick) ?? 0)
                        .filter((val): val is number => val !== undefined);
                }
                return hasDifferentUnits ? (
                    <YAxisTicks
                        formatter={rightFormatter}
                        position="rightY"
                        customTicksYValues={customTicksYValues}
                        units={
                            rightYScaleUnit
                                ? `(${_.get(languageData, `unit.${rightYScaleUnit}`)})`
                                : undefined
                        }
                        unitsColor={BARS_INFO.BEV_UP.colorValue}
                    />
                ) : null;
            }}
        </GraphContext.Consumer>
    );

    return (
        <div
            className="graph bg-white margin-bottom-1 position-relative overflow-hidden"
            style={{ height: dimensions.height + dimensions.legendHeight }}
            data-test="vehicle-two-up-bars-TruE-graph"
        >
            {hoverInfo && (
                <>
                    {hasNoData && (
                        <Overlay hasData x={hoverInfo.leftX + hoverInfoXOffset} y={HOVER_Y_OFFSET}>
                            <FormattedMessage id="noData" />
                        </Overlay>
                    )}
                    {hasSomeData && (
                        <>
                            {renderLeftOverlay()}
                            {renderRightOverlay()}
                        </>
                    )}
                </>
            )}
            <GraphDataProvider
                formatDate={formatDate}
                formatMessage={formatMessage}
                dimensions={dimensions}
                data={data}
                hoverInfoXOffset={hoverInfoXOffset}
                setHoverInfoXOffset={setHoverInfoXOffset}
                yScaleShouldStartAtZero
                graphType={GraphType.TWO_UP_BARS}
                shouldRoundYScales
                leftYScaleUnit={leftYScaleUnit}
                rightYScaleUnit={rightYScaleUnit}
            >
                <SegmentableDayOverlay
                    leftUpYAlignment={Alignments.RIGHT}
                    leftUpYColor={BARS_INFO.ICE.colorName}
                    leftYScaleFormatter={leftYScaleFormatter}
                    rightUpYColor={BARS_INFO.BEV_UP.colorValue}
                    rightYScaleFormatter={rightYScaleFormatter}
                    shouldShowRightUpYValue
                    useOuterXPosition
                    xOffset={hoverInfoXOffset}
                />
                <svg
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMinYMin meet"
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                >
                    <XAxis
                        tickFormatter={tickFormatter}
                        selectedElement={_.get(hoverInfo, 'data')}
                        dateRange={dateRange}
                        shouldHideTickLine
                    />
                    <YAxisLines tickGenerator={leftTickGenerator} />
                    <YAxisTicks
                        formatter={leftFormatter}
                        tickGenerator={leftTickGenerator}
                        units={
                            leftYScaleUnit
                                ? `(${_.get(languageData, `unit.${leftYScaleUnit}`)})`
                                : undefined
                        }
                        unitsColor={BARS_INFO.ICE.colorValue}
                    />
                    {renderRightYAxis()}
                    <GraphContext.Consumer>
                        {({ setHoverInfoXOffset }) => (
                            <GroupedBars
                                colorSchema={dieselAndElectricBarsSchema}
                                minHeight={0}
                                setHoverInfoXOffset={setHoverInfoXOffset}
                            />
                        )}
                    </GraphContext.Consumer>
                    <SegmentableDay
                        onHover={setHover}
                        useOuterXPosition
                        xOffset={hoverInfoXOffset}
                    />
                    <BarsLegend barsInfo={twoUpBarsInfo} />
                </svg>
            </GraphDataProvider>
        </div>
    );
};

export default TwoUpBarsGraph;
